import { Link } from '@tanstack/react-router'
import { useAuth } from '../../contexts/AuthContext'
import { Brandlock } from './Brandlock'
import { ClubLinkButton } from './ClubButton'
import { CLUB_AREAS } from './clubAreas'
import { Container } from './Container'
import { MemberAvatar } from './MemberAvatar'

/**
 * Clubhouse chrome (components card §VII; nav order incl. The Outings per the
 * `the-outings` card) — the brandlock, the member areas, and the account
 * block. The active area carries the pin-flag underline. The areas are
 * `CLUB_AREAS`, the same list the public foot's "The Club" column prints, so
 * the two cannot drift.
 *
 * The clubhouse is publicly browsable in this build, so this renders signed-out
 * too: the account block becomes a sign-in link rather than the nav vanishing.
 * Same wrap-instead-of-hide treatment as ClubHeader.
 */
export function MemberNav() {
  const { isAuthenticated, user } = useAuth()
  const memberName = user?.display_name || user?.email

  return (
    <header className="sticky top-0 z-20 border-b border-brass/35 bg-paper/90 backdrop-blur-md">
      <Container className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 py-3 sm:min-h-[70px] lg:flex-nowrap lg:py-0">
        <Brandlock />

        <nav aria-label="Clubhouse" className="flex flex-wrap items-center gap-x-7 gap-y-2">
          {CLUB_AREAS.map((area) => (
            <Link
              key={area.to}
              to={area.to}
              className="rounded-club-sm text-[13.5px] font-medium text-ink transition-colors hover:text-fairway"
              activeProps={{
                className: 'border-b-2 border-flag pb-1 text-fairway',
                'aria-current': 'page',
              }}
            >
              {area.label}
            </Link>
          ))}
        </nav>

        {isAuthenticated ? (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Link
              to="/clubhouse/your-game"
              className="rounded-club-sm text-[13px] font-semibold text-fairway underline decoration-brass underline-offset-4"
              activeProps={{
                className: 'border-b-2 border-flag pb-1 no-underline',
                'aria-current': 'page',
              }}
            >
              Your Game
            </Link>
            <Link
              to="/clubhouse/account"
              className="flex items-center gap-2.5 rounded-club-sm"
              // The accessible name keeps the visible name in it (WCAG 2.5.3
              // Label in Name): a voice-control user says what they see.
              aria-label={`Your account — ${memberName ?? 'Member'}`}
            >
              <MemberAvatar name={memberName} />
              <span className="text-[13px] font-semibold text-pine">{memberName ?? 'Member'}</span>
            </Link>
          </div>
        ) : (
          <ClubLinkButton to="/sign-in" variant="ghost" size="sm">
            Sign in
          </ClubLinkButton>
        )}
      </Container>
    </header>
  )
}
